import { Cubit } from "blac-next";
import { ShippingAddressData } from "./ShippingAddressData";
import { AddressValidationResult } from "./addressValidationTypeAliases";
import {
  AddressesControllerService,
  AddressValidationRequest
} from "@9amhealth/openapi";
import reportErrorSentry from "src/lib/reportErrorSentry";

import { shippingAddressValidationCubitConstant as constants } from "./ShippingAddressValidationCubit.constant";
import { ProcessedValidationResult } from "./AddressValidationResult";
import {
  ADDRESS_SUGGESTION_WEIGHT_LIMIT,
  extractConfirmationLevel,
  getAddressConfirmationWeight,
  validationResult2Address
} from "./addressValidation.constant";

interface ShippingAddressValidationState {
  address?: ShippingAddressData;
  validationResult: AddressValidationResult | null;
  isLoading: boolean;
}

export class ShippingAddressValidationCubit extends Cubit<ShippingAddressValidationState> {
  constructor() {
    super({ validationResult: null, isLoading: false });
  }

  public emit(state: ShippingAddressValidationState) {
    // Any update that contains no `validationResult` should invalidate the last
    // result
    super.emit({
      ...{ validationResult: null },
      ...state
    });
  }

  /**
   * Validates an address without updating the state
   * @param address
   */
  public readonly validateAddressDirect = async (
    address: ShippingAddressData
  ) => {
    try {
      return (
        (
          (
            await AddressesControllerService.validateAddress(
              address as AddressValidationRequest
            )
          ).data as { result?: AddressValidationResult }
        ).result ?? null
      );
    } catch (e: unknown) {
      reportErrorSentry(e);
      return null;
    }
  };

  /**
   * Validates an address and updates the state accordingly.
   * @param address
   */
  public readonly validateAddress = async (address: ShippingAddressData) => {
    this.patch({ address, isLoading: true });

    const validationResult = await this.validateAddressDirect(address);

    // Always maintain a valid state - even if another request was fired parallelly
    // No need for error checks here, the result will be null in case of problems occurred
    this.patch({
      address,
      validationResult,
      isLoading: false
    });
  };

  /**
   * Calls an address validating api and provides assessment on the provided address.
   * Any suggestion made by the api will be included in the result.
   * See: [ValidationResult - response from validation](https://developers.google.com/maps/documentation/address-validation/reference/rest/v1/TopLevel/validateAddress#ValidationResult)
   */
  public readonly validateShippingAddressWithApi = async (args: {
    startLoading?: () => void;
    endLoading?: () => void;
    loadingError?: () => void;
    loadingSuccess?: () => void;
    data: ShippingAddressData;
  }) => {
    const startLoading = args.startLoading ?? constants.startLoading;
    const endLoading = args.endLoading ?? constants.endLoading;
    const loadingError = args.loadingError ?? constants.loadingError;
    const loadingSuccess = args.startLoading ?? constants.loadingSuccess;
    const { data } = args;

    startLoading();

    return new Promise<ProcessedValidationResult>((resolve, reject) => {
      // Next step: Full address validation by address api (Google)
      void this.validateAddressDirect(data)
        .then((result) => {
          if (!result) {
            loadingError();
            reject(new Error("Address validation failed"));
            return;
          }
          const confirmationWeight = getAddressConfirmationWeight(
            extractConfirmationLevel(result)
          );
          const suggestion = validationResult2Address({
            result,
            fallback: data
          });
          const offerSuggestion =
            suggestion.differsFromFallback ||
            confirmationWeight > ADDRESS_SUGGESTION_WEIGHT_LIMIT;

          if (offerSuggestion) {
            endLoading();
          } else {
            loadingSuccess();
          }
          resolve({
            offerSuggestion,
            confirmationWeight,
            suggestion
          });
          return;
        })
        .catch(reject);
    });
  };
}
